import { useState, useEffect } from 'react';
import { useAuth } from '../components/auth/AuthComponents';
import { API_BASE, apiRequest } from '../utils/api';
import { toast } from 'react-toastify';

export const useSubscription = () => {
  const { token, user, isAuthenticated } = useAuth();
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentHistory, setPaymentHistory] = useState([]);

  // Fetch usage information
  const fetchUsage = async () => {
    const currentToken = localStorage.getItem('docxsai-token');
    if (!currentToken || !isAuthenticated) return;

    try {
      const usageData = await apiRequest('subscription/usage');
      setUsage(usageData);
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  // Fetch available subscription plans
  const fetchPlans = async () => {
    try {
      const plansData = await apiRequest('subscription/plans');
      setPlans(plansData.plans || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  // Fetch active subscription details
  const fetchActiveSubscription = async () => {
    // Get token directly from localStorage to avoid timing issues
    const currentToken = localStorage.getItem('docxsai-token');
    
    if (!currentToken || !isAuthenticated) {
      return;
    }

    try {
      const subData = await apiRequest('subscription/current');
      
      // The response has a subscriptions array, get the first (most recent) one
      const activeSub = subData.subscriptions && subData.subscriptions.length > 0 
        ? subData.subscriptions[0] 
        : null;
      
      setActiveSubscription(activeSub);
    } catch (error) {
      if (error.message.includes('404')) {
        // No active subscription (free plan)
        setActiveSubscription(null);
      } else {
        console.error('Error fetching active subscription:', error);
      }
    }
  };

  // Fetch payment history
  const fetchPaymentHistory = async () => {
    const currentToken = localStorage.getItem('docxsai-token');
    if (!currentToken || !isAuthenticated) return;

    try {
      const historyData = await apiRequest('subscription/payment-history');
      setPaymentHistory(historyData);
    } catch (error) {
      console.error('Error fetching payment history:', error);
    }
  };

  // Initiate payment for plan upgrade
  const initiatePayment = async (planName, billingCycle = 'monthly', redirectUrl = window.location.origin) => {
    if (!token || !isAuthenticated) {
      toast.error('Please sign in to upgrade your plan');
      return null;
    }

    try {
      const paymentData = await apiRequest('payments/initiate', {
        method: 'POST',
        body: JSON.stringify({
          plan_name: planName,
          billing_cycle: billingCycle,
          redirect_url: redirectUrl
        })
      });
      
      // Redirect to Flutterwave payment page
      window.open(paymentData.payment_link, '_blank');
      return paymentData;
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error(error.message || 'Network error. Please try again.');
      return null;
    }
  };

  // Verify payment status
  const verifyPayment = async (transactionReference) => {
    if (!token || !isAuthenticated) return;
    try {
      const result = await apiRequest(`payments/verify/${transactionReference}`);
      return result;
    } catch (error) {
      toast.error(error.message || 'Failed to verify payment.');
      return null;
    }
  };

  // Cancel subscription
  const cancelSubscription = async () => {
    if (!token || !isAuthenticated) return false;

    try {
      const cancelData = await apiRequest('/subscription/cancel', {
        method: 'POST'
      });
      
      toast.success(cancelData.message);
      await fetchActiveSubscription();
      return true;
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      toast.error(error.message || 'Network error. Please try again.');
      return false;
    }
  };

  // Helper functions
  const getPlanHierarchy = () => ({
    free: 0,
    starter: 1,
    professional: 2,
    enterprise: 3
  });

  const canUpgrade = (targetPlan) => {
    if (!user) return false;
    const hierarchy = getPlanHierarchy();
    const currentLevel = hierarchy[user.subscription_plan] || 0;
    const targetLevel = hierarchy[targetPlan] || 0;
    return targetLevel > currentLevel;
  };

  const canDowngrade = (targetPlan) => {
    if (!user) return false;
    const hierarchy = getPlanHierarchy();
    const currentLevel = hierarchy[user.subscription_plan] || 0;
    const targetLevel = hierarchy[targetPlan] || 0;
    return targetLevel < currentLevel;
  };

  const isCurrentPlan = (planName) => {
    return user?.subscription_plan === planName;
  };

  const formatPrice = (priceInKobo) => {
    return `₦${(priceInKobo / 100).toLocaleString()}`;
  };

  const getUsagePercentage = () => {
    if (!usage) return 0;
    return Math.round((usage.current_month?.total_documents || 0) / (usage.monthly_limit || 1) * 100);
  };

  const hasReachedLimit = () => {
    if (!usage) return false;
    return (usage.current_month?.total_documents || 0) >= (usage.monthly_limit || 0);
  };

  // Initial data fetch
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      await Promise.all([
        fetchUsage(),
        fetchPlans(),
        fetchActiveSubscription(),
        fetchPaymentHistory()
      ]);
      setLoading(false);
    };

    if (isAuthenticated) {
      fetchAllData();
    } else {
      // Still fetch plans for unauthenticated users
      fetchPlans();
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  return {
    // Data
    usage,
    plans,
    activeSubscription,
    paymentHistory,
    loading,
    
    // Actions
    initiatePayment,
    verifyPayment,
    cancelSubscription,
    fetchUsage,
    fetchPlans,
    fetchActiveSubscription,
    
    // Helpers
    canUpgrade,
    canDowngrade,
    isCurrentPlan,
    formatPrice,
    getUsagePercentage,
    hasReachedLimit,
    getPlanHierarchy
  };
}; 